import { useState, useEffect } from 'react';
import { AuthRoleEnum, MfaMethodEnum, useCreateUserSignupMutation } from '@codezo/graphql';
import { Box, Button, Flex, Group, Paper, ScrollArea, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/common/Logo';
import { AuthenticatorSetupStep } from '../components/signup/AuthenticatorSetupStep';
import { EmailVerificationStep } from '../components/signup/EmailVerificationStep';

/**
 * Helper function to determine redirect URL for B2C users
 */
function getUserAppRedirectUrl(): string {
	return import.meta.env.VITE_USER_APP_URL || 'http://localhost:3003';
}

interface SignupStep {
	id: string;
	title: string;
	description: string;
	component: React.ComponentType<{
		data: Record<string, unknown>;
		onDataChange: (_data: Record<string, unknown>) => void;
		onValidSubmit: (_data: Record<string, unknown>) => void;
		onPrevious: () => void;
		isFirstStep: boolean;
		isLastStep: boolean;
		triggerValidation: number;
	}>;
}

/**
 * Personal Info Step Component for B2C User Signup
 */
function PersonalInfoStep({
	data,
	onDataChange,
	onValidSubmit,
	isFirstStep: _isFirstStep,
	triggerValidation,
}: {
	data: Record<string, unknown>;
	onDataChange: (_data: Record<string, unknown>) => void;
	onValidSubmit: (_data: Record<string, unknown>) => void;
	onPrevious: () => void;
	isFirstStep: boolean;
	isLastStep: boolean;
	triggerValidation: number;
}) {
	const [errors, setErrors] = useState<Record<string, string>>({});

	const handleChange = (field: string, value: string) => {
		onDataChange({ ...data, [field]: value });
		// Clear error when user types
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: '' }));
		}
	};

	const handleMfaChange = (method: MfaMethodEnum) => {
		onDataChange({ ...data, mfaMethod: method });
	};

	const validate = () => {
		const newErrors: Record<string, string> = {};

		if (!data.firstName || !(data.firstName as string).trim()) {
			newErrors.firstName = 'First name is required';
		}
		if (!data.lastName || !(data.lastName as string).trim()) {
			newErrors.lastName = 'Last name is required';
		}
		if (!data.email || !(data.email as string).trim()) {
			newErrors.email = 'Email is required';
		} else if (!/\S+@\S+\.\S+/.test(data.email as string)) {
			newErrors.email = 'Email is invalid';
		}
		if (!data.password || (data.password as string).length < 8) {
			newErrors.password = 'Password must be at least 8 characters';
		}
		if (!data.mfaMethod) {
			newErrors.mfaMethod = 'Please select a verification method';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	useEffect(() => {
		if (triggerValidation > 0) {
			if (validate()) {
				onValidSubmit(data);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [triggerValidation]);

	return (
		<Box>
			<Title order={2} mb="md">
				Create Your Account
			</Title>
			<Text c="dimmed" mb="xl">
				Sign up to get started
			</Text>

			<Flex direction="column" gap="md">
				{/* First Name */}
				<div>
					<Text size="sm" fw={500} mb={4}>
						First Name
					</Text>
					<input
						type="text"
						value={(data.firstName as string) || ''}
						onChange={(e) => handleChange('firstName', e.target.value)}
						placeholder="Enter your first name"
						style={{
							width: '100%',
							padding: '8px 12px',
							border: `1px solid ${errors.firstName ? '#fa5252' : '#ced4da'}`,
							borderRadius: '4px',
							fontSize: '14px',
						}}
					/>
					{errors.firstName && (
						<Text size="sm" c="red" mt={4}>
							{errors.firstName}
						</Text>
					)}
				</div>

				{/* Last Name */}
				<div>
					<Text size="sm" fw={500} mb={4}>
						Last Name
					</Text>
					<input
						type="text"
						value={(data.lastName as string) || ''}
						onChange={(e) => handleChange('lastName', e.target.value)}
						placeholder="Enter your last name"
						style={{
							width: '100%',
							padding: '8px 12px',
							border: `1px solid ${errors.lastName ? '#fa5252' : '#ced4da'}`,
							borderRadius: '4px',
							fontSize: '14px',
						}}
					/>
					{errors.lastName && (
						<Text size="sm" c="red" mt={4}>
							{errors.lastName}
						</Text>
					)}
				</div>

				{/* Email */}
				<div>
					<Text size="sm" fw={500} mb={4}>
						Email
					</Text>
					<input
						type="email"
						value={(data.email as string) || ''}
						onChange={(e) => handleChange('email', e.target.value)}
						placeholder="Enter your email"
						style={{
							width: '100%',
							padding: '8px 12px',
							border: `1px solid ${errors.email ? '#fa5252' : '#ced4da'}`,
							borderRadius: '4px',
							fontSize: '14px',
						}}
					/>
					{errors.email && (
						<Text size="sm" c="red" mt={4}>
							{errors.email}
						</Text>
					)}
				</div>

				{/* Password */}
				<div>
					<Text size="sm" fw={500} mb={4}>
						Password
					</Text>
					<input
						type="password"
						value={(data.password as string) || ''}
						onChange={(e) => handleChange('password', e.target.value)}
						placeholder="Create a password (min 8 characters)"
						style={{
							width: '100%',
							padding: '8px 12px',
							border: `1px solid ${errors.password ? '#fa5252' : '#ced4da'}`,
							borderRadius: '4px',
							fontSize: '14px',
						}}
					/>
					{errors.password && (
						<Text size="sm" c="red" mt={4}>
							{errors.password}
						</Text>
					)}
				</div>

				{/* MFA Method Selection */}
				<div>
					<Text size="sm" fw={500} mb={8}>
						Choose Verification Method
					</Text>
					<Flex direction="column" gap="sm">
						<Paper
							p="md"
							withBorder
							style={{
								cursor: 'pointer',
								borderColor:
									data.mfaMethod === MfaMethodEnum.Email ? '#228be6' : '#ced4da',
								backgroundColor:
									data.mfaMethod === MfaMethodEnum.Email ? '#f0f7ff' : 'white',
							}}
							onClick={() => handleMfaChange(MfaMethodEnum.Email)}
						>
							<Flex align="center" gap="sm">
								<input
									type="radio"
									checked={data.mfaMethod === MfaMethodEnum.Email}
									onChange={() => handleMfaChange(MfaMethodEnum.Email)}
									style={{ cursor: 'pointer' }}
								/>
								<div>
									<Text fw={500}>📧 Email Verification</Text>
									<Text size="sm" c="dimmed">
										Receive a 6-digit code via email
									</Text>
								</div>
							</Flex>
						</Paper>

						<Paper
							p="md"
							withBorder
							style={{
								cursor: 'pointer',
								borderColor:
									data.mfaMethod === MfaMethodEnum.Authenticator
										? '#228be6'
										: '#ced4da',
								backgroundColor:
									data.mfaMethod === MfaMethodEnum.Authenticator
										? '#f0f7ff'
										: 'white',
							}}
							onClick={() => handleMfaChange(MfaMethodEnum.Authenticator)}
						>
							<Flex align="center" gap="sm">
								<input
									type="radio"
									checked={data.mfaMethod === MfaMethodEnum.Authenticator}
									onChange={() => handleMfaChange(MfaMethodEnum.Authenticator)}
									style={{ cursor: 'pointer' }}
								/>
								<div>
									<Text fw={500}>🔐 Authenticator App</Text>
									<Text size="sm" c="dimmed">
										Use Google Authenticator or similar apps
									</Text>
								</div>
							</Flex>
						</Paper>
					</Flex>
					{errors.mfaMethod && (
						<Text size="sm" c="red" mt={4}>
							{errors.mfaMethod}
						</Text>
					)}
				</div>
			</Flex>
		</Box>
	);
}

const baseSignupSteps: SignupStep[] = [
	{
		id: 'personal',
		title: 'Personal Information',
		description: 'Create your account',
		component: PersonalInfoStep,
	},
	{
		id: 'mfa-setup',
		title: 'Security Setup',
		description: 'Set up two-factor authentication',
		component: PersonalInfoStep, // Placeholder, will be replaced dynamically
	},
];

export function UserSignupPage() {
	const [currentStep, setCurrentStep] = useState(0);
	const [formData, setFormData] = useState<Record<string, unknown>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [triggerValidation, setTriggerValidation] = useState(0);
	const [signupSteps, setSignupSteps] = useState<SignupStep[]>(baseSignupSteps);
	const [userCreated, setUserCreated] = useState(false);

	const navigate = useNavigate();
	const [createUserSignup] = useCreateUserSignupMutation();

	const handleSubmitUser = async (completeFormData: Record<string, unknown>) => {
		setIsSubmitting(true);

		try {
			const { data } = await createUserSignup({
				variables: {
					input: {
						firstName: completeFormData.firstName as string,
						lastName: completeFormData.lastName as string,
						email: completeFormData.email as string,
						password: completeFormData.password as string,
						mfaMethod: completeFormData.mfaMethod as MfaMethodEnum,
					},
				},
			});

			if (data?.createUserSignup) {
				const { userId, qrCodeDataURL } = data.createUserSignup;

				// Store necessary data for MFA setup
				sessionStorage.setItem('pendingUserRole', AuthRoleEnum.User);

				// Add MFA data to form data
				const mfaData = {
					userId,
					qrCodeDataURL,
					email: completeFormData.email,
				};

				// Update form data with MFA info
				setFormData((prev) => ({ ...prev, ...mfaData }));

				// Mark user as created
				setUserCreated(true);

				// Update the 2nd step with the appropriate MFA component
				const mfaMethod = completeFormData.mfaMethod as MfaMethodEnum;
				const mfaComponent =
					mfaMethod === MfaMethodEnum.Email
						? EmailVerificationStep
						: AuthenticatorSetupStep;

				// Update the last step with the correct MFA component
				setSignupSteps((prev) => {
					const updated = [...prev];
					updated[1] = {
						...updated[1],
						component: mfaComponent,
						title:
							mfaMethod === MfaMethodEnum.Email
								? 'Email Verification'
								: 'Authenticator Setup',
						description:
							mfaMethod === MfaMethodEnum.Email
								? 'Verify your email address'
								: 'Set up your authenticator app',
					};
					return updated;
				});

				// Move to MFA setup step
				setCurrentStep(currentStep + 1);
				setIsSubmitting(false);

				// Reset validation trigger
				setTriggerValidation(0);
			}
		} catch {
			notifications.show({
				title: 'Error',
				message: 'Failed to create account. Please try again.',
				color: 'red',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleNext = async (stepData: Record<string, unknown>) => {
		const updatedFormData = { ...formData, ...stepData };
		setFormData(updatedFormData);

		// Check if this is the MFA setup completion
		if (stepData.mfaSetupComplete) {
			// MFA setup is complete, redirect to the user app
			const authToken = stepData.authToken as string;
			const redirectUrl = getUserAppRedirectUrl();

			// Save backup codes if they exist
			if (stepData.backupCodes) {
				// In a real app, you might want to show these in a modal first
				// Backup codes are saved in the step data
			}

			// Redirect with the auth token
			window.location.href = `${redirectUrl}/auth/callback?token=${authToken}`;
			return;
		}

		// Check if we're at the personal info step (step 0)
		if (currentStep === 0 && !userCreated) {
			// This is the personal info step, need to create the user
			await handleSubmitUser(updatedFormData);
			return;
		}

		// Otherwise move to next step
		setCurrentStep((prev) => Math.min(prev + 1, signupSteps.length - 1));
		setTriggerValidation(0);
	};

	const handlePrevious = () => {
		// Can't go back after user is created (prevents duplicate user creation)
		if (userCreated) return;

		setCurrentStep((prev) => Math.max(prev - 1, 0));
		setTriggerValidation(0);
	};

	const handleContinue = () => {
		setTriggerValidation((prev) => prev + 1);
	};

	const CurrentStepComponent = signupSteps[currentStep]?.component;

	return (
		<Box
			style={{
				minHeight: '100vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: '#f8f9fa',
			}}
		>
			<Paper
				shadow="sm"
				p="xl"
				radius="md"
				style={{
					width: '100%',
					maxWidth: '600px',
					margin: '20px',
				}}
			>
				{/* Logo */}
				<Flex justify="center" mb="xl">
					<Logo />
				</Flex>

				{/* Progress Indicator */}
				<Group justify="center" mb="xl" gap="xs">
					{signupSteps.map((step, index) => (
						<Box
							key={step.id}
							style={{
								width: index === currentStep ? '32px' : '8px',
								height: '8px',
								borderRadius: '4px',
								backgroundColor: index <= currentStep ? '#228be6' : '#e9ecef',
								transition: 'all 0.3s ease',
							}}
						/>
					))}
				</Group>

				{/* Step Content */}
				<ScrollArea style={{ height: '500px' }} scrollbarSize={8}>
					<AnimatePresence mode="wait">
						<motion.div
							key={currentStep}
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -20 }}
							transition={{ duration: 0.2 }}
						>
							{CurrentStepComponent && (
								<CurrentStepComponent
									data={formData}
									onDataChange={(newData) =>
										setFormData((prev) => ({ ...prev, ...newData }))
									}
									onValidSubmit={handleNext}
									onPrevious={handlePrevious}
									isFirstStep={currentStep === 0}
									isLastStep={currentStep === signupSteps.length - 1}
									triggerValidation={triggerValidation}
								/>
							)}
						</motion.div>
					</AnimatePresence>
				</ScrollArea>

				{/* Navigation Buttons */}
				<Group justify="space-between" mt="xl">
					{currentStep > 0 && !userCreated ? (
						<Button variant="subtle" onClick={handlePrevious}>
							Back
						</Button>
					) : (
						<Box />
					)}

					{currentStep < signupSteps.length - 1 ? (
						<Button onClick={handleContinue} loading={isSubmitting} ml="auto">
							Continue
						</Button>
					) : null}
				</Group>

				{/* Login Link */}
				{currentStep === 0 && (
					<Text ta="center" mt="xl" size="sm">
						Already have an account?{' '}
						<Text
							component="span"
							c="blue"
							style={{ cursor: 'pointer', textDecoration: 'underline' }}
							onClick={() => navigate('/login')}
						>
							Sign In
						</Text>
					</Text>
				)}
			</Paper>
		</Box>
	);
}
